import axios from "axios";
import { normalizeHttpError } from "./httpError";
import { installRetry, SIGNED_RESOURCE_TIMEOUT_MS } from "./resilience";
import { installSimulatedLatency } from "./simulatedLatency";

/**
 * Cross-origin signed-resource GET 경계.
 *
 * Owns:
 * - 백엔드가 발급한 signed URL(예: GCS sample JSON)을 브라우저에서 직접 GET하는 정책.
 * - withCredentials 비활성화.
 *
 * Does not own:
 * - 내부 registry API 호출(`apiClient`가 baseURL/JWT를 소유). signed URL은 다른 origin이다.
 *   그래서 Authorization 헤더를 보내지 않는다.
 * - signed URL 발급. 이 client는 전용 API 클라이언트가 발급한 URL만 받아 fetch한다.
 * - timeout/retry 기본값(`resilience`가 소유). 이 client는 GET 전용이다. 그래서 재시도를 설치한다.
 */

const signedResourceClient = axios.create({
  withCredentials: false,
  timeout: SIGNED_RESOURCE_TIMEOUT_MS,
});

installSimulatedLatency(signedResourceClient);
// 재시도는 정규화 인터셉터보다 먼저 등록한다. 그래야 원본 AxiosError를 먼저 받아 다시 요청한다.
installRetry(signedResourceClient);

signedResourceClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => Promise.reject(normalizeHttpError(error)),
);

export async function fetchSignedJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await signedResourceClient.get<T>(url, {
    signal,
    responseType: "json",
  });
  return response.data;
}

// 본문을 문자열 그대로 받는다(파일 미리보기). json 도 이 함수로 받는다. 파싱은 호출부가 한다.
// 파싱에 실패한 파일은 원문을 보여줘야 사용자가 무엇이 잘못되었는지 볼 수 있다.
export async function fetchSignedText(url: string, signal?: AbortSignal): Promise<string> {
  const response = await signedResourceClient.get<string>(url, {
    signal,
    responseType: "text",
    // axios 는 responseType=text 여도 json 형태의 본문을 파싱해 객체로 반환한다. 이 함수는 원문을
    // 보존해야 한다. 그래서 변환을 끈다.
    transformResponse: (data: string) => data,
  });
  return response.data;
}

/** 저장된 NDJSON의 필요한 바이트만 읽는다. Range를 무시한 전체 응답은 사용하지 않는다. */
export async function fetchSignedTextRange(
  url: string,
  offset: number,
  length: number,
  signal?: AbortSignal,
): Promise<string> {
  if (
    !Number.isSafeInteger(offset) ||
    offset < 0 ||
    !Number.isSafeInteger(length) ||
    length <= 0 ||
    !Number.isSafeInteger(offset + length)
  ) {
    throw new Error("유효하지 않은 상세 데이터 범위입니다.");
  }
  const response = await signedResourceClient.get<string>(url, {
    signal,
    headers: { Range: `bytes=${offset}-${offset + length - 1}` },
    responseType: "text",
    transformResponse: (data: string) => data,
  });
  if (response.status !== 206 || new TextEncoder().encode(response.data).length !== length) {
    throw new Error("선택 시간대의 상세 데이터를 읽지 못했습니다. 다시 시도해 주세요.");
  }
  return response.data;
}
